<!-- apps/frontend/src/views/UnifiedFlowEditor.vue -->
<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-gray-900 text-white" @click="paletteSourceNodeId = null">
    <!-- Header -->
    <header class="bg-gray-900 px-8 py-4 flex justify-between items-center shrink-0">
      <div class="flex items-center gap-4">
        <div v-if="flow" class="flex items-center gap-2">
          <!-- input-inline (style.css) inherits the text size/weight from this wrapper,
               kept apart from the info tooltip below. -->
          <div class="text-xl font-bold">
            <InputText v-model="flow.name" class="input-inline" :aria-label="$t('flowEditor.header.flowNameLabel')" />
          </div>
          <div class="group relative">
            <span class="material-icons text-gray-400 text-sm cursor-help">info</span>
            <div class="absolute left-0 top-6 hidden group-hover:block bg-gray-700 text-white text-xs rounded-sm py-1 px-2 whitespace-nowrap z-50">
              {{ $t('flowEditor.header.nameEditHint') }}
            </div>
          </div>
        </div>
        <p v-else class="text-xl font-bold">{{ $t('flowEditor.header.loadingText') }}</p>
      </div>
      <div class="flex items-center gap-3">
        <Button
          severity="primary"
          @click.stop="paletteSourceNodeId = '__orphan__'"
          v-tooltip.bottom="$t('flowEditor.header.addNodeTitle')"
        >
          <span class="material-icons text-sm" aria-hidden="true">add</span>
          {{ $t('flowEditor.header.addNodeButton') }}
        </Button>
        <Button
          severity="secondary"
          @click="onSave"
          :label="isSaving ? $t('flowEditor.header.savingButton') : $t('flowEditor.header.saveButton')"
          :disabled="isSaving || !hasUnsavedChanges"
          :loading="isSaving"
        >
          <template #icon>
            <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">save</span>
          </template>
        </Button>
        <span v-if="config.listRoute" class="w-px h-8 bg-gray-700" aria-hidden="true"></span>
        <Button
          v-if="config.listRoute"
          severity="secondary"
          @click="router.push({ name: config.listRoute, params: { workspaceId: route.params.workspaceId, id: route.params.id } })"
          v-tooltip.bottom="$t(config.closeTitleKey)"
        >
          <span class="material-icons text-sm" aria-hidden="true">close</span>
          {{ $t('flowEditor.header.closeButton') }}
        </Button>
      </div>
    </header>
    <div class="h-0.5 bg-gradient-to-r from-cyan-400 via-indigo-500 to-pink-500 rounded-xs mx-8 mb-6 shrink-0" aria-hidden="true"></div>

    <!-- Canvas area -->
    <div class="flex-grow relative overflow-hidden" @dragover.prevent @drop="onDrop">
      <!-- Validation error banner. The shadow and the opaque backdrop sit on the
           positioning wrapper: each Message severity sets its own box-shadow token
           (a Tailwind shadow on the Message root would lose under cssLayer: false),
           and the preset's warn/error Message backgrounds are 50% translucent, so
           without a solid base the canvas would show through the banner. -->
      <div
        v-if="banner.kind !== 'none'"
        class="absolute top-4 left-1/2 -translate-x-1/2 z-50 max-w-[min(90%,42rem)] rounded-md shadow-lg bg-gray-900"
      >
        <Message :severity="bannerStyle.severity" size="small" :pt="{ root: { role: 'status', 'aria-live': 'polite' } }">
          <template #icon>
            <span class="material-icons select-none shrink-0" aria-hidden="true">{{ bannerStyle.iconName }}</span>
          </template>
          <span v-if="banner.kind === 'stale'">{{ $t('flowEditor.validation.staleAfterEdit') }}</span>
          <span v-else-if="banner.kind === 'warnings'">{{ banner.graphMessages.join(' — ') }}</span>
          <span v-else>
            {{ banner.graphMessages.length > 0
              ? banner.graphMessages.join(' — ')
              : $t('flowEditor.validation.graphErrors', { count: banner.count })
            }}
          </span>
        </Message>
      </div>

      <VueFlow
        v-model:nodes="nodes"
        v-model:edges="edges"
        class="theme-dark"
        :is-valid-connection="isValidConnection"
        @nodes-change="onNodesChange"
        @pane-click="onPaneClick"
        @edge-click="onEdgeClick"
        @node-double-click="onNodeDoubleClick"
      >
        <Background />
        <MiniMap v-if="showMinimap" />
        <Controls>
          <ControlButton
            @click="showMinimap = !showMinimap"
            v-tooltip.right="showMinimap ? $t('flowEditor.header.hideMinimapTooltip') : $t('flowEditor.header.showMinimapTooltip')"
            :aria-label="showMinimap ? $t('flowEditor.header.hideMinimapTooltip') : $t('flowEditor.header.showMinimapTooltip')"
            :class="showMinimap ? 'text-cyan-400' : ''"
          >
            <span class="material-icons" style="font-size:14px" aria-hidden="true">map</span>
          </ControlButton>
        </Controls>

        <!-- Flow-only nodes -->
        <template #node-input="props">
          <StartNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-botSays="props">
          <BotSaysNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-botAsks="props">
          <BotAsksNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-intentVerifier="props">
          <IntentVerifierNode :data="props.data" :selected="props.selected" />
        </template>
        <template #node-botRespondWithKb="props">
          <BotRespondWithKbNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>

        <!-- Tool-only nodes -->
        <template #node-toolInput="props">
          <ToolInputNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-toolOutput="props">
          <ToolOutputNode :data="props.data" :selected="props.selected" />
        </template>

        <!-- Shared nodes -->
        <template #node-condition="props">
          <ConditionNode :data="props.data" :selected="props.selected" />
        </template>
        <template #node-ifCondition="props">
          <IfConditionNode :data="props.data" :selected="props.selected" :node-id="props.id" />
        </template>
        <template #node-setVariable="props">
          <SetVariableNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-executeCode="props">
          <ExecuteCodeNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-apiCall="props">
          <ApiCallNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id && isApiCallSuccessHandle(e.sourceHandle))" @add-node="onAddNodeClick" />
        </template>
        <template #node-webSearch="props">
          <WebSearchNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-useMcpTool="props">
          <UseMcpToolNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-addTags="props">
          <AddTagsNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <!-- Conversation nodes -->
        <template #node-sendMessage="props">
          <SendMessageNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-sendChatMessage="props">
          <SendChatMessageNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-waitForUserReply="props">
          <WaitForUserReplyNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-triggerAgent="props">
          <TriggerAgentNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-askAgent="props">
          <AskAgentNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-sendWhatsapp="props">
          <SendWhatsappNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-sendWhatsappTemplate="props">
          <SendWhatsappTemplateNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-activateTransfer="props">
          <ActivateTransferNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-deactivateTransfer="props">
          <DeactivateTransferNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-transferCall="props">
          <TransferCallNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-endCall="props">
          <EndCallNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-fetchHistory="props">
          <FetchHistoryNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <!-- Webhook-only nodes -->
        <template #node-webhookInput="props">
          <WebhookInputNode :data="props.data" :selected="props.selected" :node-id="props.id" :has-outgoing-edge="edges.some(e => e.source === props.id)" @add-node="onAddNodeClick" />
        </template>
        <template #node-webhookOutput="props">
          <WebhookOutputNode :data="props.data" :selected="props.selected" />
        </template>
      </VueFlow>

      <!-- Palette — component determined by config -->
      <component
        :is="config.paletteComponent"
        v-if="paletteSourceNodeId"
        :source-node-id="paletteSourceNodeId"
        :whatsapp-active="whatsappActive"
        :web-search-active="webSearchActive"
        :voice-active="voiceActive"
        @select="onPaletteSelect"
        @close="paletteSourceNodeId = null"
      />
    </div>

    <!-- Node Config Modal (not shown for executeCode — opens code editor directly) -->
    <NodeConfigModal
      v-if="configModalNodeId && configModalNode?.type !== 'executeCode'"
      :node="nodes.find(n => n.id === configModalNodeId)"
      :has-error-edge="edges.some(e => e.source === configModalNodeId && e.sourceHandle === 'error')"
      :bot-id="botId"
      :flow-mode="config.mode"
      :flow-nodes="nodes"
      @save="onModalSave"
      @close="configModalNodeId = null"
      @open-code-editor="isCodeEditorModalOpen = true"
    />

    <!-- Code Editor Modal (universal — fixes code node in tool flows) -->
    <CodeEditorModal
      :show="isCodeEditorModalOpen"
      :initial-code="configModalNode?.data?.code || ''"
      :initial-libs="configModalNode?.data?.libs || []"
      @close="onCodeEditorClose"
      @save="saveCodeFromModal"
    />

    <!-- Unsaved Changes Modal (universal) -->
    <UnsavedChangesModal
      :show="showUnsavedChangesModal"
      :is-saving="isSaving"
      @save="handleSaveFromModal"
      @discard="handleDiscardChanges"
      @cancel="handleStayInEditor"
    />

    <!-- Node-deleted toast with Undo: own group, since the global App.vue Toast is bottom-right and has no Undo -->
    <Toast :group="UNDO_TOAST_GROUP" position="bottom-left" @close="clearPendingUndo" @life-end="clearPendingUndo">
      <template #message="{ message }">
        <div class="flex flex-1 items-center gap-3">
          <span class="p-toast-summary">{{ message.summary }}</span>
          <Button
            variant="link"
            size="small"
            class="select-none"
            :label="$t('toast.undoButton')"
            @click="undoDelete"
          />
        </div>
      </template>
    </Toast>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls, ControlButton } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';

import { flowValidationBanner, clearNodeValidationErrors } from '../utils/flowValidationState';
import { getDefaultDataForType } from '../utils/flowNodeDefaults';
import { isVoiceChannelActive } from '../utils/flowPalette';

import CodeEditorModal    from '../components/flow/modals/CodeEditorModal.vue';
import UnsavedChangesModal from '../components/flow/modals/UnsavedChangesModal.vue';
import NodePalette       from '../components/flow/NodePalette.vue';
import ToolFlowNodePalette from '../components/flow/ToolFlowNodePalette.vue';
import WebhookFlowNodePalette from '../components/flow/WebhookFlowNodePalette.vue';
import NodeConfigModal    from '../components/flow/NodeConfigModal.vue';

// Flow-only nodes
import StartNode          from '../components/flow/nodes/StartNode.vue';
import BotSaysNode        from '../components/flow/nodes/BotSaysNode.vue';
import BotAsksNode        from '../components/flow/nodes/BotAsksNode.vue';
import IntentVerifierNode from '../components/flow/nodes/IntentVerifierNode.vue';
import BotRespondWithKbNode from '../components/flow/nodes/BotRespondWithKbNode.vue';

// Tool-only nodes
import ToolInputNode  from '../components/flow/nodes/ToolInputNode.vue';
import ToolOutputNode from '../components/flow/nodes/ToolOutputNode.vue';

// Webhook-only nodes
import WebhookInputNode  from '../components/flow/nodes/WebhookInputNode.vue';
import WebhookOutputNode from '../components/flow/nodes/WebhookOutputNode.vue';

// Shared nodes
import ConditionNode   from '../components/flow/nodes/ConditionNode.vue';
import IfConditionNode from '../components/flow/nodes/IfConditionNode.vue';
import SetVariableNode from '../components/flow/nodes/SetVariableNode.vue';
import ExecuteCodeNode from '../components/flow/nodes/ExecuteCodeNode.vue';
import ApiCallNode     from '../components/flow/nodes/ApiCallNode.vue';
import WebSearchNode   from '../components/flow/nodes/WebSearchNode.vue';
import UseMcpToolNode  from '../components/flow/nodes/UseMcpToolNode.vue';
import AddTagsNode     from '../components/flow/nodes/AddTagsNode.vue';

// Conversation nodes
import SendMessageNode           from '../components/flow/nodes/SendMessageNode.vue';
import SendChatMessageNode       from '../components/flow/nodes/SendChatMessageNode.vue';
import WaitForUserReplyNode      from '../components/flow/nodes/WaitForUserReplyNode.vue';
import SendWhatsappNode          from '../components/flow/nodes/SendWhatsappNode.vue';
import SendWhatsappTemplateNode  from '../components/flow/nodes/SendWhatsappTemplateNode.vue';
import ActivateTransferNode      from '../components/flow/nodes/ActivateTransferNode.vue';
import DeactivateTransferNode    from '../components/flow/nodes/DeactivateTransferNode.vue';
import TransferCallNode          from '../components/flow/nodes/TransferCallNode.vue';
import EndCallNode               from '../components/flow/nodes/EndCallNode.vue';
import TriggerAgentNode         from '../components/flow/nodes/TriggerAgentNode.vue';
import AskAgentNode             from '../components/flow/nodes/AskAgentNode.vue';
import FetchHistoryNode          from '../components/flow/nodes/FetchHistoryNode.vue';

import { useFlowPersistence } from '../composables/useFlowPersistence';
import { useFlowKeyboard }    from '../composables/useFlowKeyboard';
import { useWebSearchPricing } from '../composables/useWebSearchPricing';
import { localizeValidationError } from '../utils/flowValidationMessages';
import api                    from '../services/api';
import { showToast }          from '../services/toastService';
import toolFlowService        from '../services/toolFlowService';
import webhookFlowService     from '../services/webhookFlowService';
import { getWhatsappActivationStatus } from '../services/whatsappService';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';

const { t, te } = useI18n();
const route  = useRoute();
const router = useRouter();

const botId       = computed(() => route.params.id);
const workspaceId = computed(() => route.params.workspaceId);

const whatsappActive = ref(false);
const voiceActive = ref(false);

watch(
  [botId, workspaceId],
  async ([id, wsId]) => {
    whatsappActive.value = false;
    if (!id || !wsId) return;
    try {
      const res = await getWhatsappActivationStatus(wsId, id);
      whatsappActive.value = res?.data?.status === 'active';
    } catch (err) {
      Console.error('[UnifiedFlowEditor] stato WhatsApp non recuperabile:', err?.message);
      whatsappActive.value = false;
    }
  },
  { immediate: true }
);

// I nodi voce ("trasferimento di chiamata", "chiudi chiamata") hanno senso solo
// se il bot ha un canale voce attivo (Vapi o GPT-Live) — invisibili in palette
// altrimenti, coerentemente col guard a runtime negli handler.
watch(
  [botId, workspaceId],
  async ([id, wsId]) => {
    voiceActive.value = false;
    if (!id || !wsId) return;
    try {
      const res = await api.get(`/workspaces/${wsId}/bots/${id}/connectors`);
      const connectors = res?.data?.data || [];
      voiceActive.value = isVoiceChannelActive(connectors);
    } catch (err) {
      Console.error('[UnifiedFlowEditor] stato canale voce non recuperabile:', err?.message);
      voiceActive.value = false;
    }
  },
  { immediate: true }
);

// Ricerca Web è una feature globale (non per-bot): il nodo compare in
// palette solo se abilitata in amministrazione.
const { pricing: webSearchPricing, fetchPricing: fetchWebSearchPricing } = useWebSearchPricing();
const webSearchActive = computed(() => webSearchPricing.value.enabled);
onMounted(fetchWebSearchPricing);

// ── Tool mode client-side validation ──────────────────────────────────────
function validateToolFlow(nodeList, edgeList) {
  const errs = [];
  const inputNodes  = nodeList.filter(n => n.type === 'toolInput');
  const outputNodes = nodeList.filter(n => n.type === 'toolOutput');
  if (inputNodes.length !== 1) errs.push({ message: t('flowEditor.validation.toolFlow.exactlyOneInputNode') });
  if (outputNodes.length < 1)  errs.push({ message: t('flowEditor.validation.toolFlow.atLeastOneOutputNode') });
  if (inputNodes.length === 1) {
    const toolName = inputNodes[0].data?.toolName || '';
    if (!toolName || !/^[a-zA-Z0-9_]+$/.test(toolName))
      errs.push({ message: t('flowEditor.validation.toolFlow.toolNameRequired') });
  }
  if (inputNodes.length === 1 && outputNodes.length >= 1) {
    const reachable = new Set();
    const queue = [inputNodes[0].id];
    while (queue.length) {
      const cur = queue.shift();
      if (reachable.has(cur)) continue;
      reachable.add(cur);
      edgeList.filter(e => e.source === cur).forEach(e => queue.push(e.target));
    }
    if (!outputNodes.some(o => reachable.has(o.id)))
      errs.push({ message: t('flowEditor.validation.toolFlow.noInputToOutputPath') });
  }
  return errs;
}

// ── Editor configs ────────────────────────────────────────────────────────
const EDITOR_CONFIGS = {
  FlowEditor: {
    mode: 'flow',
    editorIdParam: 'flowId',
    listRoute: 'BotFlows',
    closeTitleKey: 'flowEditor.header.closeTitle',
    paletteComponent: NodePalette,
    exitNodeIndicators: true,
    validation: 'backend',
    validateFn: null,
    fetchFn: (wsId, id) => api.get(`/workspaces/${wsId}/flows/single/${id}`),
    saveFn:  (wsId, id, payload) => api.put(`/workspaces/${wsId}/flows/${id}`, payload),
    getFlowFromResponse: (res) => res.data.data,
  },
  ToolFlowEditor: {
    mode: 'tool',
    editorIdParam: 'toolFlowId',
    listRoute: 'BotTools',
    closeTitleKey: 'flowEditor.header.closeTitleTool',
    paletteComponent: ToolFlowNodePalette,
    exitNodeIndicators: false,
    validation: 'client',
    validateFn: validateToolFlow,
    fetchFn: (wsId, id) => toolFlowService.get(wsId, id),
    saveFn:  (wsId, id, payload) => toolFlowService.update(wsId, id, payload),
    getFlowFromResponse: (res) => res.data.toolFlow,
  },
  WebhookFlowEditor: {
    mode: 'webhook',
    editorIdParam: 'webhookFlowId',
    listRoute: 'BotWebhooks',
    closeTitleKey: 'flowEditor.header.closeTitleWebhook',
    paletteComponent: WebhookFlowNodePalette,
    exitNodeIndicators: false,
    validation: 'client',
    validateFn: (nodeList) => {
      const errs = [];
      if (!nodeList.some(n => n.type === 'webhookInput')) errs.push({ message: t('flowEditor.validation.webhookFlow.inputNodeRequired') });
      return errs;
    },
    fetchFn: (wsId, id) => webhookFlowService.get(wsId, id),
    saveFn:  (wsId, id, payload) => webhookFlowService.update(wsId, id, payload),
    getFlowFromResponse: (res) => res.data.webhookFlow,
  },
};

const config = EDITOR_CONFIGS[route.name];
if (!config) throw new Error(`[UnifiedFlowEditor] Unknown route name: ${route.name}`);
const editorId = computed(() => route.params[config.editorIdParam]);

// ── Vue Flow instance ─────────────────────────────────────────────────────
const { project, addNodes, onConnect, addEdges, getSelectedElements, removeNodes, removeEdges,
        findNode, nodesSelectionActive, onNodeDragStart, onNodeDragStop, updateNode,
        fitView, addSelectedNodes, setCenter } = useVueFlow();

// ── Core state ────────────────────────────────────────────────────────────
const nodes            = ref([]);
const edges            = ref([]);
const flow             = ref(null);
const showMinimap      = ref(true);
const validationErrors = ref([]);

// ── UI state ──────────────────────────────────────────────────────────────
const paletteSourceNodeId   = ref(null);
const configModalNodeId     = ref(null);
const selectedEdge          = ref(null);
const isCodeEditorModalOpen = ref(false);
const nodeSelectedBeforeDrag = ref(null);

// ── Undo-delete toast ─────────────────────────────────────────────────────
// Rendered by the <Toast :group="UNDO_TOAST_GROUP"> in the template; the shared
// constant keeps the add/removeGroup calls and that instance in step.
const toast            = useToast();
const UNDO_TOAST_GROUP = 'flow-undo';
const UNDO_TOAST_LIFE  = 5000;
// Last deleted node + its edges: restorable (Undo button / Ctrl+Z) only while
// its toast is on screen, so it is dropped when the toast expires or is closed.
let pendingUndo = null;
const clearPendingUndo = () => { pendingUndo = null; };

// ── Persistence ───────────────────────────────────────────────────────────
const {
  isSaving, hasUnsavedChanges, validationStale, showUnsavedChangesModal, pendingNavigation,
  checkForChanges, saveOriginalFlowState, computeExitNodeIds, fetchFlow, saveFlow,
} = useFlowPersistence({
  workspaceId, editorId, config, nodes, edges, flow,
  // Gli errori del backend portano un codice: tradotti qui, nella lingua dell'utente
  localizeError: (error) => localizeValidationError(error, { t, te }),
});

// Gli errori arrivano dal backend al caricamento e a ogni salvataggio, o dalla
// validazione lato client che blocca il salvataggio: appena il grafo cambia
// rispetto a quello validato sono riferiti a un canvas che non esiste piu'. Vedi
// utils/flowValidationState.js.
const banner = computed(() => flowValidationBanner({
  validationErrors: validationErrors.value,
  stale: validationStale.value,
}));

// Un warning non deve leggersi come un errore: il flusso e' salvato e valido.
const BANNER_STYLES = {
  stale:    { severity: 'secondary', iconName: 'edit' },
  warnings: { severity: 'warn',      iconName: 'warning_amber' },
  errors:   { severity: 'error',     iconName: 'error' },
};
const bannerStyle = computed(() => BANNER_STYLES[banner.value.kind] || BANNER_STYLES.errors);
const setValidationErrors = (errs) => { validationErrors.value = errs; };
const configModalNode = computed(() =>
  configModalNodeId.value ? nodes.value.find(n => n.id === configModalNodeId.value) : null
);

// ── Keyboard ──────────────────────────────────────────────────────────────
useFlowKeyboard({
  onDelete: () => {
    if (configModalNodeId.value) return;
    const activeNode = nodes.value.find(n => n.selected);
    if (activeNode) { performDelete(activeNode); return; }
    if (selectedEdge.value) { onDeleteEdge(selectedEdge.value); return; }
    if (nodesSelectionActive.value) {
      removeNodes(getSelectedElements.value.filter(el => el.source === undefined));
      removeEdges(getSelectedElements.value.filter(el => el.source !== undefined));
      checkForChanges();
    }
  },
  onUndo: undoDelete,
  onEscape: () => {
    if (configModalNodeId.value) { configModalNodeId.value = null; return; }
    if (paletteSourceNodeId.value) { paletteSourceNodeId.value = null; return; }
    selectedEdge.value = null;
    nodes.value.forEach(n => { n.selected = false; });
  },
});


let nodeIdCounter = 1;
const getNextId = () => `dndnode_${nodeIdCounter++}`;

// ── Palette / node creation ───────────────────────────────────────────────
// Stessa regola dell'handler (apps/backend/src/services/flow/handlers/apiCall.js):
// il ramo di successo e' l'handle senza id, che il manifest chiama "default".
const isApiCallSuccessHandle = (sourceHandle) => !sourceHandle || sourceHandle === 'default';

const onAddNodeClick = (sourceNodeId) => {
  if (configModalNodeId.value) return;
  paletteSourceNodeId.value = sourceNodeId;
};

const onPaletteSelect = (nodeType) => {
  const isOrphan = paletteSourceNodeId.value === '__orphan__';
  const sourceNode = isOrphan ? null : findNode(paletteSourceNodeId.value);
  if (!sourceNode && !isOrphan) { paletteSourceNodeId.value = null; return; }

  const newNodeId = getNextId();
  const newNode = {
    id: newNodeId,
    type: nodeType,
    position: sourceNode
      ? { x: sourceNode.position.x + 280, y: sourceNode.position.y }
      : { x: 300, y: 200 },
    data: getDefaultDataForType(nodeType),
  };
  addNodes([newNode]);
  if (sourceNode)
    addEdges([{ id: `e-${paletteSourceNodeId.value}-${newNodeId}`, source: paletteSourceNodeId.value, target: newNodeId }]);
  if (config.exitNodeIndicators) nextTick(updateExitNodeIndicators);
  checkForChanges();
  paletteSourceNodeId.value = null;
};

// ── Node interactions ─────────────────────────────────────────────────────
const onNodeDoubleClick = ({ node }) => {
  if (configModalNodeId.value) return;
  configModalNodeId.value = node.id;
  if (node.type === 'executeCode') {
    isCodeEditorModalOpen.value = true;
  }
};

const onModalSave = (updatedData) => {
  const node = findNode(configModalNodeId.value);
  if (node) {
    updateNode(node.id, { data: updatedData });
    // I pannelli riscrivono i data senza le chiavi dell'editor: il badge di uscita va ricalcolato.
    if (config.exitNodeIndicators) nextTick(updateExitNodeIndicators);
    checkForChanges();
  }
  configModalNodeId.value = null;
};

const onPaneClick = () => {
  paletteSourceNodeId.value = null;
  selectedEdge.value = null;
  nodes.value.forEach(n => { n.selected = false; });
};

const onEdgeClick = ({ edge }) => {
  selectedEdge.value = edge;
  nodes.value.forEach(n => { n.selected = false; });
  paletteSourceNodeId.value = null;
};

const onDeleteEdge = (edge) => {
  removeEdges([edge.id]);
  selectedEdge.value = null;
  if (config.exitNodeIndicators) nextTick(updateExitNodeIndicators);
  checkForChanges();
};

const onNodesChange = (changes) => {
  changes.forEach(change => {
    if (change.type === 'select') {
      nodes.value.forEach(node => { node.selected = node.id === change.id && change.selected; });
      if (change.selected) selectedEdge.value = null;
    }
  });
};

onConnect((params) => {
  addEdges(params);
  if (config.exitNodeIndicators) nextTick(updateExitNodeIndicators);
});

onNodeDragStart(() => {
  const sel = nodes.value.find(n => n.selected);
  if (sel) nodeSelectedBeforeDrag.value = sel;
});

onNodeDragStop(() => {
  if (nodeSelectedBeforeDrag.value) {
    const node = findNode(nodeSelectedBeforeDrag.value.id);
    if (node) node.selected = true;
    nodeSelectedBeforeDrag.value = null;
  }
});

const updateExitNodeIndicators = () => {
  const exitNodeIds = computeExitNodeIds(nodes.value, edges.value);
  nodes.value = nodes.value.map(n => ({
    ...n,
    data: { ...n.data, __isExitNode: exitNodeIds.has(n.id) },
  }));
};

const onDrop = (event) => {
  const type = event.dataTransfer?.getData('application/vueflow');
  if (!type) return;
  const { left, top } = event.target.getBoundingClientRect();
  const position = project({ x: event.clientX - left, y: event.clientY - top });
  addNodes([{ id: getNextId(), type, position, data: getDefaultDataForType(type) }]);
  if (config.exitNodeIndicators) nextTick(updateExitNodeIndicators);
  checkForChanges();
};

// ── Delete / undo ─────────────────────────────────────────────────────────
const performDelete = (node) => {
  if (!node) return;
  const removedNode  = { ...node };
  const removedEdges = edges.value.filter(e => e.source === node.id || e.target === node.id);
  removeNodes([node]);
  removeEdges(removedEdges);
  if (config.exitNodeIndicators) nextTick(updateExitNodeIndicators);
  checkForChanges();

  // A new delete replaces the pending toast (and its undo payload). removeGroup
  // emits no close/life-end, and the old message's timer dies with it.
  toast.removeGroup(UNDO_TOAST_GROUP);
  pendingUndo = { node: removedNode, edges: removedEdges };
  toast.add({ group: UNDO_TOAST_GROUP, severity: 'info', summary: t('toast.nodeDeleted'), life: UNDO_TOAST_LIFE });
};

function undoDelete() {
  if (!pendingUndo) return;
  const { node, edges: removedEdges } = pendingUndo;
  addNodes([node]);
  addEdges(removedEdges);
  if (config.exitNodeIndicators) nextTick(updateExitNodeIndicators);
  checkForChanges();
  clearPendingUndo();
  toast.removeGroup(UNDO_TOAST_GROUP);
}

// ── Code editor modal ─────────────────────────────────────────────────────
const onCodeEditorClose = () => {
  isCodeEditorModalOpen.value = false;
  if (configModalNode.value?.type === 'executeCode') {
    configModalNodeId.value = null;
  }
};

const saveCodeFromModal = (data) => {
  if (configModalNode.value) {
    const node = findNode(configModalNode.value.id);
    if (node) { node.data.code = data.code; node.data.libs = data.libs; checkForChanges(); }
  }
};

// ── Save ──────────────────────────────────────────────────────────────────
// Un salvataggio bloccato dalla validazione si spiega da solo (banner ed
// errori sui nodi); una richiesta fallita invece non lascerebbe traccia.
const notifySaveFailed = () => showToast(t('flowEditor.toast.saveError'), 'error');

const onSave = async () => {
  const outcome = await saveFlow(setValidationErrors);
  if (outcome === 'failed') notifySaveFailed();
};

// ── Route guard ───────────────────────────────────────────────────────────
onBeforeRouteLeave((to, from, next) => {
  if (hasUnsavedChanges.value) {
    pendingNavigation.value = to.fullPath;
    showUnsavedChangesModal.value = true;
    next(false);
  } else {
    next();
  }
});

const isValidConnection = (connection) => {
  if (!connection.source) return true;
  const srcHandle = connection.sourceHandle || null;
  return !edges.value.some(e =>
    e.id !== connection.id &&
    e.source === connection.source &&
    (e.sourceHandle || null) === srcHandle
  );
};

const handleSaveFromModal = async () => {
  const outcome = await saveFlow(setValidationErrors);
  // Richiesta fallita: il dialogo resta aperto per riprovare o scartare.
  // Chiuderlo e navigare farebbe scattare di nuovo la guardia (le modifiche
  // sono ancora da salvare), che lo riaprirebbe senza alcuna spiegazione.
  if (outcome === 'failed') {
    notifySaveFailed();
    return;
  }
  showUnsavedChangesModal.value = false;
  if (outcome === 'invalid') {
    // Si resta nell'editor a correggere gli errori segnalati.
    pendingNavigation.value = null;
    return;
  }
  if (pendingNavigation.value) {
    router.push(pendingNavigation.value);
    pendingNavigation.value = null;
  }
};

const handleStayInEditor = () => {
  showUnsavedChangesModal.value = false;
  pendingNavigation.value = null;
};

const handleDiscardChanges = () => {
  showUnsavedChangesModal.value = false;
  if (pendingNavigation.value) {
    hasUnsavedChanges.value = false;
    router.push(pendingNavigation.value);
    pendingNavigation.value = null;
  }
};

// ── Fit to view ───────────────────────────────────────────────────────────
// All'apertura il flusso si adatta sempre alla vista, cosi' si vede per intero
// a prescindere da come lo si era lasciato: lo zoom volutamente non si salva.
watch(
  () => flow.value?.definition,
  async () => {
    await nextTick();
    // Deep link ?node=<id> (da "Errori da sistemare"): seleziona e centra il nodo;
    // se non esiste piu' si apre il flusso intero, senza selezione.
    if (focusNodeFromQuery()) return;
    fitView({ padding: 0.2, duration: 400 });
  }
);

// Una sola volta: ai salvataggi successivi la definizione cambia ma la vista non va ricentrata.
let nodeFocusConsumed = false;
const focusNodeFromQuery = () => {
  const nodeId = route.query.node;
  if (!nodeId || nodeFocusConsumed) return false;
  nodeFocusConsumed = true;
  const n = findNode(String(nodeId));
  if (!n) return false;
  addSelectedNodes([n]);
  setCenter(
    n.position.x + (n.dimensions?.width || 0) / 2,
    n.position.y + (n.dimensions?.height || 0) / 2,
    { zoom: 1.2, duration: 400 },
  );
  return true;
};

// ── Dirty tracking ────────────────────────────────────────────────────────
watch([nodes, edges], () => checkForChanges(), { deep: true, flush: 'sync' });

// I marker di errore sui nodi seguono lo stesso destino del banner: stantii
// appena il grafo cambia, quindi si spengono in attesa della prossima validazione.
watch(validationStale, (stale) => {
  if (stale) nodes.value = clearNodeValidationErrors(nodes.value);
});
watch(() => flow.value?.name, () => { if (flow.value) checkForChanges(); });

// ── Mount ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  const result = await fetchFlow(router);
  if (result) {
    nodeIdCounter = result.maxId + 1;
    if (result.validationErrors?.length) setValidationErrors(result.validationErrors);
    await nextTick();
    saveOriginalFlowState();
  }
});
</script>

<style>
.vue-flow__minimap {
  background-color: #1f2937;
  border: 1px solid #374151;
  border-radius: 0.5rem;
  overflow: hidden;
}

.vue-flow__controls {
  background-color: #1f2937;
  border: 1px solid #374151;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.4);
  overflow: hidden;
  padding: 0;
}

.vue-flow__controls-button {
  background-color: #1f2937;
  border: none;
  border-bottom: 1px solid #374151;
  color: #9ca3af;
  width: 32px;
  height: 32px;
  padding: 7px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.vue-flow__controls-button:last-child {
  border-bottom: none;
}

.vue-flow__controls-button svg {
  fill: #9ca3af;
  max-width: 14px;
  max-height: 14px;
}

.vue-flow__controls-button:hover {
  background-color: #374151;
  color: white;
}

.vue-flow__controls-button:hover svg {
  fill: white;
}

.vue-flow__controls-button.text-cyan-400 svg {
  fill: #22d3ee;
}

.vue-flow__controls-button.text-cyan-400:hover svg {
  fill: #67e8f9;
}

.vue-flow__node-input,
.vue-flow__node-botSays,
.vue-flow__node-botAsks,
.vue-flow__node-condition,
.vue-flow__node-ifCondition,
.vue-flow__node-setVariable,
.vue-flow__node-executeCode,
.vue-flow__node-apiCall,
.vue-flow__node-webSearch,
.vue-flow__node-intentVerifier,
.vue-flow__node-useMcpTool,
.vue-flow__node-botRespondWithKb,
.vue-flow__node-toolInput,
.vue-flow__node-toolOutput {
  background: transparent; border: none; padding: 0; width: auto; height: auto;
}

.vue-flow__node-sendMessage,
.vue-flow__node-triggerAgent,
.vue-flow__node-askAgent,
.vue-flow__node-activateTransfer,
.vue-flow__node-deactivateTransfer,
.vue-flow__node-transferCall,
.vue-flow__node-endCall,
.vue-flow__node-fetchHistory,
.vue-flow__node-webhookInput {
  width: auto;
}

.vue-flow__handle { width: 12px !important; height: 12px !important; border-width: 2px !important; }
.vue-flow__handle:hover { width: 16px !important; height: 16px !important; }

</style>
